import { StatusBadge, statusToVariant } from './StatusBadge';
import { RefreshButton } from './RefreshButton';
import { formatRelativeTime } from '@/lib/format';
import type { AdminMatrixCell } from './types';

/**
 * `<AmenityCell>` — single matrix cell. Renders count (mono), relative
 * timestamp, status badge, and a per-cell refresh button. The per-cell
 * button maps to county scope (entire county) since the API doesn't
 * support per-cell refresh — see web/routes/api.js POST /api/admin/refresh
 * which accepts only `scope: 'county' | 'category' | 'all'`.
 *
 * Threat T-09-05: cell.gap_reason is rendered into the StatusBadge tooltip
 * via the `title` HTML attribute (browser-escaped). Never injected via
 * raw-HTML APIs.
 */
export interface AmenityCellProps {
  cell: AdminMatrixCell;
  /** Plural lower-case label, e.g. 'dog parks' / 'beaches'. Used in aria-label. */
  categoryLabel: string;
  /** Title-cased county label, e.g. 'Pinellas County'. */
  countyLabel: string;
  pending?: boolean;
  onRefresh: () => void;
}

export function AmenityCell({
  cell,
  categoryLabel,
  countyLabel,
  pending = false,
  onRefresh,
}: AmenityCellProps) {
  const variant = statusToVariant(cell.status);
  return (
    <td className="p-3 align-middle border border-white/5">
      <div className="flex items-center gap-2">
        <span className="font-mono text-sm font-semibold text-foam-50 tabular-nums">
          {cell.count}
        </span>
        <span className="text-xs text-foam-50/60">
          {cell.last_checked_at ? formatRelativeTime(cell.last_checked_at) : '—'}
        </span>
      </div>
      <div className="mt-1 flex items-center gap-2">
        <StatusBadge variant={variant} tooltip={cell.gap_reason ?? undefined} />
        <RefreshButton
          ariaLabel={`Refresh ${categoryLabel} in ${countyLabel}`}
          onClick={onRefresh}
          pending={pending}
        />
      </div>
    </td>
  );
}
